import { authController } from '@reedexpo/authorization-component-ui';
import React from 'react';
import PropTypes from 'prop-types';
import Classes from '../common/Classes';
import AlgoliaTokenService from './AlgoliaTokenService';
import TranslationService from '../translations';
import SpinnerWithMessage from '../common/components/SpinnerWithMessage';
import ErrorContainer from '../common/components/ErrorContainer';
import FeedbackButton from '../common/components/FeedbackButton';
import SurveyPopupWrapper from '../common/components/SurveyPopupWrapper';
import AlgoliaWrapper from './AlgoliaWrapper';
import { setMomentLocale } from '../common/dateHelpers';
import ShowPlanService from '../common/api/ShowPlanService';
import FavouriteUpdateListener from './FavouriteUpdateListener';
import NetworkingService from './NetworkingService';
import { updateNetworkFollowees } from '../common/updateFolloweeActions';
import authorizationStore from '../common/api/authorizationStore';
import UserService from '../common/api/UserService';
import Utils from '../common/Utils';
import CommonService from '../common/api/CommonService';
import TrackerWrapper from '../common/tracker';
import Tags from '../common/dtmTags';
import { Survey } from '../common/Constants';
import { isNewAlgoliaIndexEnabled } from '../common/eventEditions';

let tracker;

class ParticipantDirectory extends React.Component {
  constructor(props) {
    super(props);
    setMomentLocale(this.props.context.interfaceLocale);
    tracker = TrackerWrapper.getTracker();
    this.state = {
      isLoading: true,
      apiKey: null,
      errors: [],
      showPlan: {},
      digitalPortfolioName: null,
      networkFollowees: [],
      networkFolloweesUpdated: false,
      isNetworkParticipant: false,
      loggedInUserId: null,
      participations: null,
      isCurrentEEAdmin: false,
      hasAdminCalled: false,
      isAnonymousRegistrationEnabled: false
    };
  }

  componentDidMount() {
    const { context, baseApiUrl, showPlanId } = this.props;
    const usePkce = typeof (window) === 'undefined' ? false : (window && window.authSettings && window.authSettings.usePkce);

    CommonService.getEventEditionProperties(baseApiUrl, this.props.context.eventEditionId, this.props.context.interfaceLocale)
      .then((response) => {
        this.setIntialState(response, context, baseApiUrl);
      });
    this.usePkce(usePkce);
    this.getAlgoliaApiKey();
    if (context.showPlanningEnabled) {
      this.getCurrentShowPlan(context, baseApiUrl, showPlanId);
      window.addEventListener('pageshow', this.handlePageLoadedFromCache);
    }

    authorizationStore.getToken().then(async (token) => {
      setTimeout(async () => {
        tracker.track('Participant Directory Visited', {
          'Participant User ID': `${await authorizationStore.getCurrentLoggedInParticipantId(token)}`,
          'Organisation ID': `${await authorizationStore.getCurrentLoggedInRxOrgId(token)}`,
          'Product Area': 'Participant'
        });
      }, 3000);
      const hasAdminClaim = await authorizationStore.hasAdminClaim(token);
      this.setAdminState(hasAdminClaim, baseApiUrl, context);

      const tokenObject = await authorizationStore.verifyToken(token);
      const userId = tokenObject?.sub || '';
      UserService.getUserParticipation({
        baseApiUrl,
        eventEditionId: context.eventEditionId,
        userId,
        locale: context.interfaceLocale
      }).then(({ userParticipation = null }) => {
        this.setUserParticipationState(userParticipation);
      }).catch((errors) => {
        this.setState({ errors, isLoading: false });
      });
    }).catch((errors) => {
      this.setState({ errors, isLoading: false });
    });
    CommonService.getEventEditionProperties(baseApiUrl, this.props.context.eventEditionId, this.props.context.interfaceLocale).then((response) => {
      this.setState({ isAnonymousRegistrationEnabled: response?.eventEdition?.isAnonymousRegistrationEnabled });
    });
  }

  getCurrentLoggedInUserId = () => {
    authorizationStore.getToken()
      .then(async (token) => {
        if (token === null || token === undefined) {
          this.setState({ loggedInUserId: '' });
        }
        const tokenObject = await authorizationStore.verifyToken(token);
        this.setState({ loggedInUserId: tokenObject?.sub || '' });
      })
      .catch(() => this.setState({ loggedInUserId: '' }));
  };

  setNetworkFolloweesUpdated = () => {
    this.setState({ networkFolloweesUpdated: true });
  };

  getNetworkFollowees = (baseApiUrl, eventEditionId, locale) => {
    NetworkingService.getNetworkFollowees(baseApiUrl, eventEditionId, locale)
      .then((response) => {
        if (response.networkFollowees && response.errors.length === 0) {
          this.setNetworkFolloweesUpdated();
          this.setState({ networkFollowees: response.networkFollowees });
        }
      }).catch((errors) => {
        this.setState({ errors, isLoading: false });
      });
  };

  getCurrentShowPlan = (context, baseApiUrl, showPlanId) => {
    ShowPlanService.getShowPlan(baseApiUrl, context.eventEditionId, showPlanId)
      .then((serviceResult) => {
        this.setState({ showPlan: serviceResult.showPlan });
      });
  };

  getAlgoliaApiKey = () => {
    AlgoliaTokenService.getAlgoliaToken(this.props.baseApiUrl, this.props.context.eventEditionId, this.props.context.interfaceLocale)
      .then((response) => {
        this.setState({ apiKey: response.result, isLoading: false });
      }).catch((errors) => {
        this.setState({ isLoading: false, errors });
      });
  };

  setAdminState = (hasAdminClaim, baseApiUrl, context) => {
    if (hasAdminClaim) {
      UserService.getEventEditionListForAdmin(baseApiUrl, context.interfaceLocale, context.eventEditionId).then((eventEditionList) => {
        const matchedEventEdition = Array.isArray(eventEditionList.userExhibitingOrganisations)
          ? Utils.findCurrentEventEdition(eventEditionList.userExhibitingOrganisations, context.eventEditionId) : null;
        if (matchedEventEdition) {
          this.setState({
            isCurrentEEAdmin: true
          });
        }
        this.setState({
          isLoading: false,
          hasAdminCalled: true
        });
      }).catch(() => {
        this.setState({
          hasAdminCalled: true
        });
      });
    } else {
      this.setState({
        hasAdminCalled: true
      });
    }
  };

  setIntialState = (response, context, baseApiUrl) => {
    this.setState(
      {
        isAnonymousRegistrationEnabled: response?.eventEdition?.isAnonymousRegistrationEnabled
      },
      () => {
        this.fetchNetworkFollowees(baseApiUrl, context.eventEditionId, context);
        this.getCurrentLoggedInUserId();
      }
    );
  };

  setUserParticipationState = (userParticipation) => {
    this.setState({
      isLoading: false,
      participations: userParticipation && userParticipation.length > 0 ? userParticipation[0] : null
    });
  };

  fetchNetworkFollowees = (baseApiUrl, eventEditionId, context) => {
    this.setState({ isLoading: false, isNetworkParticipant: true });
    this.getNetworkFollowees(baseApiUrl, eventEditionId, context.interfaceLocale);
    this.setNetworkFolloweesUpdated();
  };

  usePkce = (usePkce) => {
    if (usePkce) {
      authController.addAccessTokenRenewedHandler(() => {
        this.renewAlgoliaApiKey();
      });
    }
  };

  renewAlgoliaApiKey = () => {
    AlgoliaTokenService.renewAlgoliaToken(this.props.baseApiUrl, this.props.context.eventEditionId, this.props.context.interfaceLocale)
      .then((response) => {
        this.setState({ apiKey: response.result, isLoading: false });
      }).catch((errors) => {
        this.setState({ isLoading: false, errors });
      });
  };

  handlePageLoadedFromCache = (event) => {
    if (event.persisted) {
      this.getCurrentShowPlan(this.props.context, this.props.baseApiUrl, this.props.showPlanId);
    }
  };

  // timeoutRef = null;

  updatedShowPlan = (updatedShowPlan) => {
    this.setState({
      showPlan: updatedShowPlan
    });
    this.fetchNetworkFollowees(this.props.baseApiUrl, this.props.context.eventEditionId, this.props.context);
  };

  updateFolloweeList = (followeeId, action) => {
    const { networkFollowees } = this.state;
    const updatedNetworkFollowees = updateNetworkFollowees(networkFollowees, followeeId, action);
    this.setState({ networkFollowees: updatedNetworkFollowees });
  };

  renderCustomComponent = () => {
    const {
      baseApiUrl, context, algoliaConfig, navigation, assets
    } = this.props;

    const {
      isLoading, apiKey, errors, digitalPortfolioName, networkFollowees, isNetworkParticipant, loggedInUserId, hasAdminCalled
    } = this.state;
    if (isLoading) {
      const translations = TranslationService.getTranslations(context.interfaceLocale);
      return <SpinnerWithMessage isBig message={translations.loader.participantDirectoryLoader} />;
    }

    if (errors.length !== 0 && hasAdminCalled) {
      return <ErrorContainer errors={errors} />;
    }

    const isNewIndex = isNewAlgoliaIndexEnabled(context.eventEditionId);

    const indexName = isNewIndex ? `${context.eventId}-participants` : `${context.eventEditionId}_${context.interfaceLocale.toLowerCase()}`;
    const translations = TranslationService.getTranslations(context.interfaceLocale);

    return (this.state.networkFolloweesUpdated
      ? (
        <AlgoliaWrapper
          baseApiUrl={baseApiUrl}
          eventEditionId={context.eventEditionId}
          locale={context.interfaceLocale}
          appId={algoliaConfig.participantPortalAppId || algoliaConfig.protectedAppId}
          indexName={indexName}
          apiKey={apiKey}
          navigation={navigation}
          context={context}
          assets={assets}
          showPlan={this.state.showPlan}
          showPlanningEnabled={context.showPlanningEnabled}
          digitalPortfolioId={context.eventEditionId}
          digitalPortfolioName={digitalPortfolioName}
          networkFollowees={networkFollowees}
          updateFolloweeList={this.updateFolloweeList}
          isNetworkParticipant={isNetworkParticipant}
          loggedInUserId={loggedInUserId}
          pageTitle={translations.directory.pageTitle}
          isAnonymousRegistrationEnabled={this.state.isAnonymousRegistrationEnabled}
        />
      )
      : null
    );
  };

  renderSurveyPopup = (surveyTranslation) => (
    <div>
      <SurveyPopupWrapper
        surveyUri={`${Survey.SurveyLink}?eve_id=${this.props.context.eventEditionId}`}
        spinnerMessage={surveyTranslation.spinnerMessage}
      />
      <FeedbackButton feedbackText={surveyTranslation.feedBackButtonText} dataDtm={Tags.participantDirectory.feedbackButton} />
    </div>
  );

  render() {
    const { homePage } = Classes;
    const { context } = this.props;
    const {
      isCurrentEEAdmin, participations, hasAdminCalled
    } = this.state;
    const translations = TranslationService.getTranslations(context.interfaceLocale);
    const errorMessage = [{
      code: 'admin',
      message: translations.directory.AdminCannotViewMessage
    }];
    return (
      <div>
        {/* eslint-disable-next-line no-nested-ternary */}
        {!hasAdminCalled ? <SpinnerWithMessage isBig message={translations.loader.participantDirectoryLoader} />
          : (isCurrentEEAdmin && !(participations && participations.length !== 0))
            ? <ErrorContainer errors={errorMessage} />
            : (
              <div className={homePage.rootElement}>
                <FavouriteUpdateListener
                  showPlan={this.state.showPlan}
                  showPlanningEnabled={this.props.context.showPlanningEnabled}
                  favouriteCallback={this.updatedShowPlan}
                  unfavouriteCallback={this.updatedShowPlan}
                />
                {this.renderCustomComponent()}
                {this.renderSurveyPopup(translations.directory.survey)}
              </div>
            )}
      </div>
    );
  }
}

ParticipantDirectory.propTypes = {
  algoliaConfig: PropTypes.shape({
    participantPortalAppId: PropTypes.string,
    protectedAppId: PropTypes.string
  }).isRequired,
  baseApiUrl: PropTypes.string.isRequired,
  context: PropTypes.shape({
    interfaceLocale: PropTypes.string.isRequired,
    primaryLocale: PropTypes.string.isRequired,
    eventEditionId: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired,
    showPlanningEnabled: PropTypes.bool.isRequired
  }).isRequired,
  navigation: PropTypes.object.isRequired,
  assets: PropTypes.shape({
    defaultParticipantPhotoMobileUrl: PropTypes.string.isRequired,
    defaultParticipantPhotoUrl: PropTypes.string.isRequired
  }).isRequired,
  showPlanId: PropTypes.string.isRequired
};

export default ParticipantDirectory;
